import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CircleDollarSign, TrendingUp, Users, FileUp, Sparkles, Database,
  Globe, Bot, Zap, Bell, AlertTriangle, ChevronRight, Server } from "lucide-react";

const StatusDot = ({ ok }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
);

const Metric = ({ icon: Icon, label, value, sub, accent = "#2563eb", testid }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <p className="text-2xl font-bold" style={{ color: accent }}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export default function SystemAnalytics({ lang, onGoto }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/system-analytics"); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !d) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const b = d.business, s = d.system;
  const sev = { high: "#ef4444", medium: "#d97706", low: "#64748b" };

  return (
    <div className="space-y-8" data-testid="admin-system">
      {/* Notifications */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2"><Bell className="w-4 h-4" />{L("Centro notifiche", "Notifications")}</h3>
        {d.notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-xl border border-border p-4" data-testid="notif-empty">{L("Tutto in ordine, nessuna notifica.", "All clear, no notifications.")}</p>
        ) : (
          <div className="space-y-2" data-testid="notif-list">
            {d.notifications.map((n, i) => (
              <motion.button key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => n.link && onGoto(n.link)} disabled={!n.link} data-testid={`notif-${n.type}-${i}`}
                className={`w-full text-left rounded-xl border border-border bg-card p-3 flex items-center gap-3 ${n.link ? "hover:border-primary/50 cursor-pointer" : "cursor-default"}`}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${sev[n.severity]}1a`, color: sev[n.severity] }}>
                  <AlertTriangle className="w-4 h-4" />
                </span>
                <span className="flex-1"><span className="font-bold">{n.count}</span> · {n.title}</span>
                {n.link && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </motion.button>
            ))}
          </div>
        )}
      </section>

      {/* Business analytics */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Analytics business", "Business analytics")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={CircleDollarSign} label="MRR" value={`€${b.mrr.toLocaleString()}`} sub={`${b.active_paid} ${L("paganti", "paying")}`} accent="#16a34a" testid="biz-mrr" />
          <Metric icon={TrendingUp} label={L("Conversione prova→pagamento", "Trial→paid")} value={`${b.conversion_pct}%`} sub={`${b.trials} ${L("in prova", "on trial")}`} accent="#7c3aed" testid="biz-conversion" />
          <Metric icon={Users} label={L("Nuovi clienti (mese)", "New clients (month)")} value={b.new_clients_month} accent="#0891b2" testid="biz-newclients" />
          <Metric icon={CircleDollarSign} label={L("Ricavo incassato", "Collected revenue")} value={`€${b.revenue_total.toLocaleString()}`} sub={`${b.payments_paid} ${L("pagamenti", "payments")}`} accent="#16a34a" testid="biz-revenue" />
          <Metric icon={FileUp} label={L("Import totali", "Total imports")} value={b.imports_total} sub={`${b.imports_month} ${L("questo mese", "this month")}`} accent="#2563eb" testid="biz-imports" />
          <Metric icon={Zap} label={L("Prezzi consigliati", "Price recs")} value={b.price_recs_total} sub={`${b.price_recs_month} ${L("questo mese", "this month")}`} accent="#d97706" testid="biz-pricerecs" />
          <Metric icon={Sparkles} label={L("Analisi AI eseguite", "AI analyses")} value={b.ai_analyses_total} sub={L("visibilità + foto", "visibility + photos")} accent="#db2777" testid="biz-ai" />
        </div>
      </section>

      {/* System health */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Salute sistema", "System health")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="sys-status">
          {[["Database", Database, s.database === "ok"], ["Google API", Globe, s.google_api === "configured"],
            ["AI", Bot, s.ai === "configured"], ["Apify", Server, s.apify === "configured"]].map(([label, Icon, ok]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-3 flex items-center gap-2.5">
              <Icon className="w-4 h-4 text-muted-foreground" />
              <span className="flex-1 text-sm font-medium">{label}</span>
              <StatusDot ok={ok} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Server} label={L("Chiamate Apify (mese)", "Apify runs (month)")} value={s.apify_runs_month} sub={`${s.apify_runs_total} ${L("totali", "total")}`} accent="#2563eb" testid="sys-apify-runs" />
          <Metric icon={CircleDollarSign} label={L("Costo Apify (mese)", "Apify cost (month)")} value={`$${s.apify_cost_month ?? s.apify_cost_usd}`} sub={`${s.apify_items_month ?? 0} ${L("item · $" + s.apify_cost_usd + " tot", "items · $" + s.apify_cost_usd + " total")}`} accent="#16a34a" testid="sys-apify-cost" />
          <Metric icon={Zap} label={L("Tempo medio scan", "Avg scan time")} value={`${s.apify_avg_sec}s`} accent="#d97706" testid="sys-apify-avg" />
          <Metric icon={AlertTriangle} label={L("Errori Apify", "Apify errors")} value={s.apify_errors} accent={s.apify_errors ? "#ef4444" : "#64748b"} testid="sys-apify-errors" />
        </div>
        {(s.apify_by_search_month || []).length > 0 && (
          <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3" data-testid="sys-apify-breakdown">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">{L("Chiamate del mese per fonte", "This month's calls by source")}</p>
            <div className="flex flex-wrap gap-2">
              {s.apify_by_search_month.map((r, i) => (
                <span key={i} className="text-xs bg-secondary rounded px-2 py-1 font-data">
                  {r.label === "scan-competitor" ? L("scan competitor (URL)", "competitor scan (URL)") : r.label}: <b>{r.calls}</b>
                </span>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
